package com.milta.feature.route.presentation

import com.milta.feature.imports.domain.entity.ExtractedDelivery
import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.DeliveryDraft
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.spreadsheetimport.domain.entity.ImportedDeliveryRow
import com.milta.feature.team.domain.entity.Driver

/** Mirrors the backend `RouteBatchLimits.MaxDeliveriesPerBatch` (`RouteBatches/RouteBatchLimits.cs`)
 * — checked right after import (both spreadsheet and PDF paths) so an oversized file is rejected
 * before the manager spends time reviewing/correcting rows on Écran 2, not only at "Envoyer". */
const val MAX_STOPS = 500

/**
 * Which of the mockup's screens (`docs/mockups/MIL-50.html`, Écrans 1/1b/1c/1d/2, plus Écran 3
 * added by MIL-51) is currently shown on the web dashboard's "Ajouter une tournée" flow. Mirrors
 * the mobile `SpreadsheetImportPhase` (same `POST /spreadsheet-imports` contract, MIL-27) but adds
 * [VerifyAssign]/[BatchResult] since this flow — unlike mobile — combines import, driver
 * assignment and batch dispatch confirmation on a single wizard instead of separate
 * screens/navigation.
 */
sealed interface WebImportRoutePhase {
    /** Écran 1 — dropzone, no file picked yet. */
    data object Idle : WebImportRoutePhase

    /** No dedicated mockup screen for this — a lightweight loading state on the Écran 1 dropzone
     * while `POST /spreadsheet-imports` is in flight, unavoidable since the call is asynchronous. */
    data object Analysing : WebImportRoutePhase

    /** Écran 1b — 400 `error.validation.invalid_file` / `error.import.unsupported_format`. */
    data object InvalidFile : WebImportRoutePhase

    /** Écran 1c — 422 `error.spreadsheet_extraction_failed`. */
    data object ExtractionFailed : WebImportRoutePhase

    /** Écran 1d — 503 `error.import.external_service_unavailable`. */
    data object ServiceUnavailable : WebImportRoutePhase

    /** No HTTP response at all — mirrors the mobile flow's own addition for the same gap (no
     * dedicated mockup screen), reuses [com.milta.core.localization.ImportsStrings] offline wording. */
    data object NetworkUnavailable : WebImportRoutePhase

    /** Écran 2 (+ 2b/2c/2d variants) — file imported, driver assignment + stops table. */
    data object VerifyAssign : WebImportRoutePhase

    /** Import succeeded (spreadsheet or PDF) but yielded more than [MAX_STOPS] rows — rejected
     * immediately rather than letting the manager reach Écran 2, mirrors the backend's own
     * `RouteBatchLimits.MaxDeliveriesPerBatch` check on `POST /routes`. [stopCount] is the number
     * of rows actually detected, shown in the error message. */
    data class TooManyStops(val stopCount: Int) : WebImportRoutePhase

    /** Écran 3 (MIL-51) — `POST /route-batches` succeeded, shows the per-driver split
     * ([WebImportRouteState.batchResult]) before the manager confirms with "Valider et envoyer les
     * tournées" (`POST /route-batches/{id}/dispatch`). No dedicated data here (mirrors [VerifyAssign]
     * itself): the actual payload lives in [WebImportRouteState.batchResult]/`batchStopsByRouteId`. */
    data object BatchResult : WebImportRoutePhase
}

/**
 * One row of the Écran 2 stops table — mirrors [ImportedDeliveryRow] plus a stable [id] (the
 * backend contract has no per-row identifier for spreadsheet imports, unlike `POST /imports/pdf`'s
 * `tempId`) so a single row can be corrected in place (pencil icon, "Cliquez sur le crayon pour
 * corriger une ligne") without losing track of which row changed.
 */
data class WebImportRouteRow(
    val id: Int,
    val recipientName: String?,
    val address: String?,
    val phoneNumber: String?,
    val deadlineTime: String?,
    val hasMissingFields: Boolean,
) {
    /** Mirrors the mobile flow's own note: the opaque [hasMissingFields] flag isn't relied upon —
     * only [recipientName]/[address] nullity decides whether a row is incomplete (Écran 2b). */
    val isIncomplete: Boolean get() = recipientName.isNullOrBlank() || address.isNullOrBlank()
}

fun ImportedDeliveryRow.toRouteRow(id: Int) = WebImportRouteRow(
    id = id,
    recipientName = recipientName,
    address = address,
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
    hasMissingFields = hasMissingFields,
)

/** Mirrors [ImportedDeliveryRow.toRouteRow] for the PDF path (`POST /imports/pdf`,
 * [ExtractDeliveriesFromPdfUseCase]) — [ExtractedDelivery.tempId] is a backend-issued opaque
 * string, not reused as [WebImportRouteRow.id]: the same caller-assigned index used for the
 * spreadsheet path keeps row identity consistent regardless of import source. */
fun ExtractedDelivery.toRouteRow(id: Int) = WebImportRouteRow(
    id = id,
    recipientName = recipientName,
    address = address,
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
    hasMissingFields = missingFields.isNotEmpty(),
)

/** `recipientName`/`address` are required by `POST /routes` — a still-incomplete row is coerced to
 * blank rather than dropped, so the row count sent always matches what the table showed. */
fun WebImportRouteRow.toDraft() = DeliveryDraft(
    recipientName = recipientName.orEmpty(),
    address = address.orEmpty(),
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
)

/**
 * State for the web dashboard's "Ajouter une tournée" flow (MIL-50/MIL-51 — `WebImportRouteViewModel`).
 * [submitError] holds a stable error CODE resolved to a localized string in the screen, distinct
 * from the import [phase] errors above (Écran 2d/3's inline banners, not Écran 1's full-screen ones).
 */
data class WebImportRouteState(
    val phase: WebImportRoutePhase = WebImportRoutePhase.Idle,
    val fileName: String = "",
    val totalCount: Int = 0,
    val isStructuredFormat: Boolean = true,
    val rows: List<WebImportRouteRow> = emptyList(),
    val drivers: List<Driver> = emptyList(),
    val isLoadingDrivers: Boolean = false,
    /** Écran 2 (MIL-51) — replaces the former single `selectedDriverId`: any number of the
     * enterprise's drivers can now be checked, always submitted through `POST /route-batches`
     * (MIL-39), even when this set holds exactly one id. */
    val selectedDriverIds: Set<String> = emptySet(),
    val isSubmitting: Boolean = false,
    /** "driver_required" | "already_dispatched" | "route_not_found" | "validation_error" |
     * "optimization_unavailable" | "server_error" | "offline". Shared by the Écran 2 submit
     * (`POST /route-batches`) and the Écran 3 dispatch (`POST /route-batches/{id}/dispatch`) —
     * both are inline-banner failures on their own step, never a full-screen error. */
    val submitError: String? = null,
    /** Écran 3 (MIL-51) — result of a successful `POST /route-batches`, drives
     * [com.milta.web.WebRouteBatchResultScreen]. Cleared back to null only by starting a brand new
     * import ([WebImportRouteState] default), never by a failed dispatch — the manager stays on the
     * same split to retry. */
    val batchResult: RouteBatchResult? = null,
    /** Stops of each batch route, keyed by [com.milta.feature.route.domain.entity.RouteBatchDriverProposal.routeId]
     * — fetched one `GET /routes/{id}` (MIL-38, [com.milta.feature.route.domain.usecase.GetRouteDetailUseCase])
     * per driver right after [batchResult] is set, since `POST /route-batches`'s own response carries
     * no per-stop detail (name/address), only counts. */
    val batchStopsByRouteId: Map<String, List<Delivery>> = emptyMap(),
    /** Route ids whose `GET /routes/{id}` fetch in [batchStopsByRouteId]'s loading step failed —
     * lets the card tell "couldn't load the stops" apart from "this route genuinely has few/no
     * stops" instead of silently showing an empty preview for both. */
    val batchStopsFailedRouteIds: Set<String> = emptySet(),
    val isLoadingBatchStops: Boolean = false,
    val isDispatchingBatch: Boolean = false,
    /** RoutaR kill switch (ad hoc feature, 2026-08) — fail-open default while `GET /app-config`
     * hasn't resolved yet, see AppConfigRepository. When `false`, [WebVerifyAssignRouteScreen]
     * hides "Optimiser l'itinéraire" and only offers the existing
     * [WebImportRouteIntent.SubmitWithoutOptimization] CTA, never attempting the RoutaR call that
     * would fail every time. */
    val routarEnabled: Boolean = true,
) {
    val selectedDrivers: List<Driver> get() = drivers.filter { it.id in selectedDriverIds }

    /** Derived from [rows] (mirrors [WebImportRouteRow.isIncomplete]) rather than stored — a stored
     * count computed once at import time would go stale the moment [WebImportRouteIntent.UpdateRow]
     * corrects or breaks a row, unlike the mobile equivalents (`SpreadsheetValidationState`,
     * `OcrValidationScreen`) which already recompute it the same way on every read. */
    val incompleteCount: Int get() = rows.count { it.isIncomplete }
}
